import React, { useState } from 'react';
import { KeyRound, Eye, EyeOff, AlertCircle, CheckCircle, Check, X } from 'lucide-react';
import Button from '../../components/Button';
import Card from '../../components/Card';
import { resetPassword } from '../../services/passwordResetService';
import {
  validatePassword,
  getPasswordStrengthColor,
  getPasswordStrengthText,
} from '../../utils/passwordValidation';

interface NewPasswordProps {
  email: string;
  otpCode: string;
  onPasswordReset: (message: string) => void;
  onBackToVerify: () => void;
  onBackToRequest: () => void;
  error: string | null;
  setError: (error: string | null) => void;
  message: string | null;
}

const NewPassword: React.FC<NewPasswordProps> = ({
  email,
  otpCode,
  onPasswordReset,
  onBackToVerify,
  onBackToRequest,
  error,
  setError,
  message,
}) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!newPassword.trim()) {
      setError('New password is required');
      return;
    }

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setError('Password does not meet security requirements');
      return;
    }

    if (!confirmPassword.trim()) {
      setError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await resetPassword(email, otpCode, newPassword, confirmPassword);
      onPasswordReset(response.message);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password';
      setError(errorMessage);

      if (errorMessage.includes('expired') || errorMessage.includes('Invalid')) {
        setTimeout(() => {
          onBackToRequest();
        }, 3000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto p-8">
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-[#E8FCFA] rounded-full">
            <KeyRound className="h-8 w-8 text-[#07E5D1]" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-[#1C1C1E] mb-2">Set New Password</h1>
        <p className="text-[#6B7280]">Create a new secure password for your account</p>
      </div>

      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-[#374151] mb-2">
              New Password
            </label>
            <div className="relative">
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#07E5D1] focus:border-[#07E5D1] outline-none transition-all"
                placeholder="Enter new password"
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-[#374151] transition-colors"
              >
                {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {newPassword && (
              <div className="mt-2 space-y-1">
                <div
                  className={`text-xs px-2 py-1 rounded inline-block ${getPasswordStrengthColor(validatePassword(newPassword).strength)}`}
                >
                  {getPasswordStrengthText(validatePassword(newPassword).strength)}
                </div>
              </div>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-[#374151] mb-2"
            >
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-[#E5E7EB] rounded-lg focus:ring-2 focus:ring-[#07E5D1] focus:border-[#07E5D1] outline-none transition-all"
                placeholder="Confirm new password"
                disabled={isLoading}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6B7280] hover:text-[#374151] transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="mt-1 text-xs text-red-600 flex items-center">
                <X className="h-3 w-3 mr-1" />
                Passwords do not match
              </p>
            )}
            {confirmPassword && newPassword === confirmPassword && confirmPassword !== '' && (
              <p className="mt-1 text-xs text-emerald-600 flex items-center">
                <Check className="h-3 w-3 mr-1" />
                Passwords match
              </p>
            )}
          </div>

          {newPassword && (
            <div className="bg-[#F1F5F9] rounded-lg p-4">
              <h3 className="text-sm font-medium text-[#374151] mb-2">Password Requirements:</h3>
              <div className="text-xs space-y-1">
                {validatePassword(newPassword).requirements.map((req, index) => (
                  <div key={index} className="flex items-center">
                    {req.met ? (
                      <Check className="h-3 w-3 text-emerald-600 mr-2" />
                    ) : (
                      <X className="h-3 w-3 text-red-600 mr-2" />
                    )}
                    <span className={req.met ? 'text-emerald-600' : 'text-gray-500'}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            disabled={
              isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword
            }
            className="bg-[#07E5D1] hover:bg-[#06D4C2] text-white"
          >
            {isLoading ? (
              <>
                <span className="inline-block h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={onBackToVerify}
              className="text-sm text-[#6B7280] hover:text-[#374151] transition-colors"
            >
              ← Back to verification
            </button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default NewPassword;
